<template>
  <div :class="$b()">
    <div
      :class="$b('media')"
    >
      <VisionLink
        v-if="product"
        :to="`/shop/redirect?sku=${product}`"
        :is-button="isOnProductPage"
        :class="$b('media-link')"
        @click="() => handleClick(product)"
      >
        <dynamic-img
          :class="$b('product-image')"
          :sku="product"
          placeholder-ratio="square"
        />
      </VisionLink>
      <VisionIcon
        v-else
        :class="$b('product-icon')"
        icon="renegades"
        colour="black"
        size="stretch"
      />
    </div>

    <div :class="$b('content')">
      <VisionStarRating
        :rating="review.rating"
        colour="black"
      />

      <VisionHeading
        size="h4"
        tag="h5"
        :class="$b('content-heading')"
      >
        {{ title }}
      </VisionHeading>

      <p
        :class="$b('text')"
      >
        {{ body }}
      </p>


      <p :class="$b('author')">
        <strong>{{ review.displayName.replace('+', ' ') }}</strong> —
        {{ $dayjs(review.submittedAt).fromNow() }}
      </p>

      <div
        v-if="productName !== null"
        :class="$b('reviewer-link')"
      >
        <VisionLink
          theme="gradient"
          :to="`/shop/redirect?sku=${product}`"
          :is-button="isOnProductPage"
          @click.stop="() => handleClick(product)"
        >
          {{
            `Shop ${productName}`
          }}
        </VisionLink>
      </div>

      <template v-if="reply">
        <div :class="$b('reply')">
          <h5
            :class="$b('reply-title')"
          >
            {{ $tr('Reply from SunGod') }}
          </h5>
          <div :class="$b('reply-expander')">
            <ExpandingTextBlock>
              <template #body-text>
                <p :class="$b('reply-body')">
                  {{ reply }}
                </p>
              </template>
            </ExpandingTextBlock>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { decodeUTF8 } from '~/assets/js/helpers/reviews'

export default {
  name: 'ReviewCard',

  props: {
    review: { type: Object, required: true },
    linkProductImage: { type: Boolean, default: true },
    isOnProductPage: { type: Boolean, default: false },
    short: { type: Boolean, default: false },
    productName: { type: String, default: null },
  },

  emits: ['productClick'],

  computed: {
    product() {
      return this.review.items[0]
    },
    title() {
      if (this.review.title.startsWith('B64:')) {
        return this.b64DecodeUnicode(this.review.title.split(':')[1])
      }
      return decodeUTF8(this.review.title)
    },
    body() {
      if (this.review.body.startsWith('B64:')) {
        return this.b64DecodeUnicode(this.review.body.split(':')[1])
      }
      return decodeUTF8(this.review.body).replaceAll('\\n', '\n')
    },
    reply() {
      return this.review.reply
    },
  },

  methods: {
    handleClick(product) {
      if (!this.isOnProductPage) return

      return this.$emit('productClick', product)
    },
  },
}
</script>

<style lang="postcss">
.ReviewCard {
  @apply flex flex-wrap md:flex-nowrap gap-4 items-start;
}

.ReviewCard__media {
  @apply w-1/2 aspect-square bg-grey-light rounded-md flex items-center justify-center;
}

.ReviewCard__media-link {
  @apply w-full;
}

.ReviewCard__content {
  @apply w-full;
}

.ReviewCard__content-heading {
  @apply my-1;
}

.ReviewCard__product-image {
  @apply w-full rounded-md aspect-square bg-grey-light;
}

.ReviewCard__product-icon {
  @apply opacity-30 w-7/12 max-w-[160px] m-auto;
}

.ReviewCard__top-row {
  @apply flex flex-row items-center justify-between;
}

.ReviewCard__title {
  @apply flex flex-col-reverse md:flex-col justify-between items-start
    pt-xxs pb-xs sm:py-sm;
}

.ReviewCard__title-text {
  @apply smMax:text-md mdMax:text-lg;
}

.ReviewCard__author {
  @apply text-xxs mt-2;
}

.ReviewCard__reply-body {
  @apply text-xxs;
}

.ReviewCard__timestamp {
  @apply mdMax:ml-auto mdMax:mr-0 mt-[4px]
    md:pl-sm
    whitespace-nowrap;
}

.ReviewCard__text {
  @apply text-xxs whitespace-pre-wrap;
}

.ReviewCard__reviewer {
  @apply flex justify-between items-end
    pb-sm;
}

.ReviewCard__reviewer-details {
  @apply flex flex-row items-center;
}

.ReviewCard__reviewer-name {
  @apply whitespace-nowrap
    pl-xxxs;
}

.ReviewCard__reviewer-verified {
  @apply pl-xs text-right
    text-off-black;
}

.ReviewCard__recommend {
  @apply items-center
    hidden
    pb-sm
    text-off-black;

  &--show {
    @apply flex;
  }
}

.ReviewCard__reviewer-link {
  @apply text-xxs mt-2 font-bold;
}

.ReviewCard__recommend-text {
  transform: translateY(0.4em);
}

.ReviewCard__reply {
  @apply mt-4;
}

.ReviewCard__reply-title {
  @apply text-xs font-bold;
}

.ReviewCard__reply-expander {
  @apply mt-2;
}
</style>
